<template>
  <div id="main-content">
    <Alert v-if="searchError" alertType="error" />
    <h1>
      Showing search results for
      <mark class="search-term">"{{ searchedTerm }}"</mark>
    </h1>

    <form id="site-search-bar" @submit.prevent="search(searchTerm, 1)" novalidate autocomplete="off" v-click-outside="() => suggestionVisible(false)">
      <input ref="search-bar" @focus="suggestionVisible(true)" placeholder="" aria-label="search input" id="main-search-bar" type="search" name="q" v-model="searchTerm" />

      <button ref="search-button" aria-label="search" type="submit">
        Search
        <svg>
          <use xlink:href="/assets/public/images/icons.svg#dhds-magnifier" />
        </svg>
      </button>

      <!-- search suggestion list -->
      <ul v-if="showSuggestions" class="search-key-suggestions">
        <li v-for="suggestion in suggestions" :key="`search-suggestion-${suggestion}`">
          <button @click="search(suggestion, 1)" class="non-button-style">{{ suggestion }}</button>
        </li>
      </ul>
    </form>

    <section class="search-content" :class="searchNotFound ? 'no-search-result fr-view' : 'search-results'">
      <template v-if="!loading && !searchError">
        <template v-if="searchNotFound">
          <h2>Sorry, we couldn't find any matches.</h2>

          <p>Search tips</p>

          <ul>
            <li>Check your spelling</li>
            <li>Use another search term</li>
            <li>Enter a single word</li>
            <li>Enter a more generic search term</li>
          </ul>

          <p>
            If you still can't find what you need,
            <router-link :to="{ name: CONTACT_US }">contact us</router-link>
            we'll be happy to help.
          </p>
        </template>

        <template v-else>
          <p class="search-pagination-label">{{ paginationLabel }}</p>

          <div class="result-card-list">
            <a :href="result.url" rel="bookmark" class="card listing search" v-for="(result, i) in results[`page${pageNum}`]" :key="`search-result-${i}-of-page-${pageNum}`">
              <article :href="result.url" class="card-link">
                <div class="time-type-wrapper">
                  <p class="article-topic">{{ result.objectType }}</p>
                  <time v-if="result.objectType.toLowerCase() !== 'page'" :datetime="result.publishDate">{{ result.dateFriendly }}</time>
                </div>
                <h2 class="search-result-item-title heading">{{ result.title }}</h2>
                <p class="preview">{{ result.description }}</p>
                <p class="search-result-breadcrumbs">
                  <!-- adding `null` at the end of the array after slice() to add ` / ` at the end if array is not empty -->
                  {{ result.breadcrumb.slice(0, -1).concat(null).join(' / ') }}
                  <b>{{ result.breadcrumb[result.breadcrumb.length - 1] }}</b>
                </p>
              </article>
            </a>
          </div>

          <Pagination :endPage="paginationEndPage" isSPA @toPageNumber="traverse" />
        </template>
      </template>

      <ApiLoading v-else :apiError="searchError" isMemberPortal></ApiLoading>
    </section>
  </div>
</template>

<script>
import { Alert, ApiLoading } from '@/components';
import { CONTACT_US } from '@/router/routeNames';
import SearchMixin from '@/_memberPortalSearchMixin';
import { getSearchPaginationSummaryText } from '@/utils';
import Pagination from '@general/js/components/Pagination.vue';

export default {
  name: 'search-result',

  mixins: [SearchMixin],

  components: {
    Alert,
    ApiLoading,
    Pagination,
  },

  data() {
    return {
      CONTACT_US,
      searchTerm: this.$route.query.searchText || this.$route.params.searchText ? decodeURI(this.$route.query.searchText || this.$route.params.searchText) : '',
      searchedTerm: decodeURI(this.$route.query.searchText || this.$route.params.searchText),
    };
  },

  computed: {
    paginationLabel() {
      return getSearchPaginationSummaryText(this.pageNum, this.resultTotalNum, this.defaultPageSize);
    },
  },

  watch: {
    '$route.query.searchText': {
      handler(searchText) {
        if (searchText !== this.searchTerm) {
          this.search(searchText, 1);
        }
      },
    },
  },

  mounted() {
    let existingPageNum = Number(this.getQueryString('pageNum'));

    if (existingPageNum === 0 || Number.isNaN(existingPageNum)) {
      existingPageNum = 1;
    }

    this.pageNum = existingPageNum;

    this.search(this.searchTerm, existingPageNum);
  },

  methods: {
    traverse(pageNum) {
      this.pageNum = pageNum;

      if (!Object.keys(this.results).includes(`page${pageNum}`)) {
        this.search(this.searchedTerm);
      }
    },
  },
};
</script>
